import { ref, onUnmounted } from 'vue'
import { getAuth } from 'firebase/auth'

/**
 * Composable for WebSocket connection to chat sessions
 *
 * Provides real-time updates for chat messages via WebSocket.
 * Messages are created via REST API, updates are broadcast via WebSocket.
 */
export function useChatWebSocket() {
  const ws = ref(null)
  const isConnected = ref(false)
  const connectionError = ref(null)
  const reconnectAttempts = ref(0)
  const maxReconnectAttempts = 5
  const reconnectDelay = ref(1000)  // Start with 1 second
  const currentSessionUuid = ref(null)
  const isAuthenticated = ref(false)

  // Event handlers (Set-based for multiple listeners)
  const messageCreatedHandlers = new Set()
  const messageUpdatedHandlers = new Set()
  const messageDeletedHandlers = new Set()
  const streamStartHandlers = new Set()
  const messageChunkHandlers = new Set()
  const streamCompleteHandlers = new Set()
  const errorHandlers = new Set()

  // Keep-alive ping interval
  let pingInterval = null

  /**
   * Connect to WebSocket server
   * Must call subscribeToSession after connection to receive messages
   */
  const connect = async () => {
    try {
      // Get Firebase token for authentication
      const auth = getAuth()
      if (!auth.currentUser) {
        throw new Error('[WebSocket] User is not authenticated. Cannot connect to chat WebSocket.')
      }
      const firebaseToken = await auth.currentUser.getIdToken(true)

      // Determine WebSocket URL
      let wsUrl
      if (import.meta.env.DEV) {
        // Development: connect to backend directly
        wsUrl = `ws://localhost:8000/api/3/ws`
      } else {
        // Production: use same host with wss protocol
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const host = window.location.host
        wsUrl = `${protocol}//${host}/api/3/ws`
      }

      console.log('[ChatWebSocket] Connecting to:', wsUrl)

      // Create WebSocket connection
      ws.value = new WebSocket(wsUrl)

      // Connection opened
      ws.value.onopen = () => {
        console.log('[ChatWebSocket] Connected, sending authentication...')

        // Send authentication token as first message
        ws.value.send(JSON.stringify({
          type: 'auth',
          token: firebaseToken
        }))
      }

      // Listen for messages
      ws.value.onmessage = (event) => {
        try {
          // Handle non-JSON messages (like plain text ping)
          if (typeof event.data === 'string' && !event.data.startsWith('{')) {
            console.log('[ChatWebSocket] Ping received:', event.data)
            return
          }

          const data = JSON.parse(event.data)
          console.log('[ChatWebSocket] Message received:', data)

          // Handle authentication confirmation
          if (data.type === 'authenticated') {
            console.log('[ChatWebSocket] Authenticated successfully')
            isConnected.value = true
            isAuthenticated.value = true
            connectionError.value = null
            reconnectAttempts.value = 0
            reconnectDelay.value = 1000

            // Start keep-alive ping
            startPing()

            // Re-subscribe to session if we had one before reconnection
            if (currentSessionUuid.value) {
              subscribeToSession(currentSessionUuid.value)
            }
            return
          }

          // Handle subscription confirmation
          if (data.type === 'subscribed') {
            console.log('[ChatWebSocket] Subscribed to topic:', data.topic)
            return
          }

          // Handle unsubscription confirmation
          if (data.type === 'unsubscribed') {
            console.log('[ChatWebSocket] Unsubscribed from topic:', data.topic)
            return
          }

          // Handle events from ConnectionManager (topic-based)
          if (data.type === 'event') {
            handleEvent(data.event, data.data)
            return
          }

        } catch (error) {
          console.error('[ChatWebSocket] Error parsing message:', error)
        }
      }

      // Handle errors
      ws.value.onerror = (error) => {
        console.error('[ChatWebSocket] Error:', error)
        connectionError.value = 'WebSocket connection error'
        errorHandlers.forEach(handler => handler(error))
      }

      // Handle connection close
      ws.value.onclose = (event) => {
        console.log('[ChatWebSocket] Disconnected', event.code, event.reason)
        isConnected.value = false
        isAuthenticated.value = false
        stopPing()

        // Attempt reconnection if not a normal closure
        if (event.code !== 1000 && reconnectAttempts.value < maxReconnectAttempts) {
          reconnectAttempts.value++
          console.log(`[ChatWebSocket] Reconnecting (attempt ${reconnectAttempts.value}/${maxReconnectAttempts})...`)

          setTimeout(() => {
            connect()
          }, reconnectDelay.value)

          // Exponential backoff
          reconnectDelay.value = Math.min(reconnectDelay.value * 2, 30000)
        } else if (reconnectAttempts.value >= maxReconnectAttempts) {
          connectionError.value = 'Failed to reconnect after multiple attempts'
        }
      }

    } catch (error) {
      console.error('[ChatWebSocket] Connection error:', error)
      connectionError.value = error.message
      throw error
    }
  }

  /**
   * Handle incoming WebSocket events
   */
  const handleEvent = (event, data) => {
    switch (event) {
      case 'message_created':
        console.log('[ChatWebSocket] Message created:', data)
        messageCreatedHandlers.forEach(handler => handler(data))
        break

      case 'message_updated':
        console.log('[ChatWebSocket] Message updated:', data)
        messageUpdatedHandlers.forEach(handler => handler(data))
        break

      case 'message_deleted':
        console.log('[ChatWebSocket] Message deleted:', data)
        messageDeletedHandlers.forEach(handler => handler(data))
        break

      case 'stream_start':
        console.log('[ChatWebSocket] Stream started')
        streamStartHandlers.forEach(handler => handler(data))
        break

      case 'message_chunk':
        console.log('[ChatWebSocket] Message chunk:', data.chunk)
        messageChunkHandlers.forEach(handler => handler(data))
        break

      case 'stream_complete':
        console.log('[ChatWebSocket] Stream complete:', data)
        streamCompleteHandlers.forEach(handler => handler(data))
        break

      case 'error':
        console.error('[ChatWebSocket] Error event:', data)
        connectionError.value = data.message || 'Error occurred'
        errorHandlers.forEach(handler => handler(data))
        break

      default:
        console.log('[ChatWebSocket] Unknown event:', event, data)
    }
  }

  /**
   * Subscribe to a chat session topic
   * @param {string} sessionUuid - The chat session UUID
   */
  const subscribeToSession = (sessionUuid) => {
    if (!ws.value || !isAuthenticated.value) {
      console.warn('[ChatWebSocket] Cannot subscribe - not connected or authenticated')
      return
    }

    const topic = `chat:${sessionUuid}`
    console.log('[ChatWebSocket] Subscribing to topic:', topic)

    currentSessionUuid.value = sessionUuid

    ws.value.send(JSON.stringify({
      action: 'sub',
      topic: topic
    }))
  }

  /**
   * Unsubscribe from a chat session topic
   * @param {string} sessionUuid - The chat session UUID
   */
  const unsubscribeFromSession = (sessionUuid) => {
    if (!ws.value || !isAuthenticated.value) {
      console.warn('[ChatWebSocket] Cannot unsubscribe - not connected or authenticated')
      return
    }

    const topic = `chat:${sessionUuid}`
    console.log('[ChatWebSocket] Unsubscribing from topic:', topic)

    // Clear current session if it matches
    if (currentSessionUuid.value === sessionUuid) {
      currentSessionUuid.value = null
    }

    ws.value.send(JSON.stringify({
      action: 'unsub',
      topic: topic
    }))
  }

  /**
   * Disconnect WebSocket
   */
  const disconnect = () => {
    if (ws.value) {
      console.log('[ChatWebSocket] Disconnecting...')
      stopPing()
      ws.value.close(1000, 'Client disconnecting')
      ws.value = null
      isConnected.value = false
      isAuthenticated.value = false
      currentSessionUuid.value = null
    }
  }

  /**
   * Send a ping to keep connection alive
   */
  const ping = () => {
    if (ws.value && isConnected.value) {
      ws.value.send(JSON.stringify({ type: 'ping' }))
    }
  }

  /**
   * Start keep-alive ping interval (every 30 seconds)
   */
  const startPing = () => {
    if (pingInterval) clearInterval(pingInterval)
    pingInterval = setInterval(ping, 30000)
    console.log('[ChatWebSocket] Started keep-alive ping')
  }

  /**
   * Stop keep-alive ping interval
   */
  const stopPing = () => {
    if (pingInterval) {
      clearInterval(pingInterval)
      pingInterval = null
      console.log('[ChatWebSocket] Stopped keep-alive ping')
    }
  }

  /**
   * Register handler for message created events
   * @param {Function} handler - Callback function(messageData)
   * @returns {Function} Unsubscribe function
   */
  const onMessageCreated = (handler) => {
    messageCreatedHandlers.add(handler)
    return () => messageCreatedHandlers.delete(handler)
  }

  /**
   * Register handler for message updated events
   * @param {Function} handler - Callback function(messageData)
   * @returns {Function} Unsubscribe function
   */
  const onMessageUpdated = (handler) => {
    messageUpdatedHandlers.add(handler)
    return () => messageUpdatedHandlers.delete(handler)
  }

  /**
   * Register handler for message deleted events
   * @param {Function} handler - Callback function({message_uuid})
   * @returns {Function} Unsubscribe function
   */
  const onMessageDeleted = (handler) => {
    messageDeletedHandlers.add(handler)
    return () => messageDeletedHandlers.delete(handler)
  }

  /**
   * Register handler for stream start events
   * @param {Function} handler - Callback function(data)
   * @returns {Function} Unsubscribe function
   */
  const onStreamStart = (handler) => {
    streamStartHandlers.add(handler)
    return () => streamStartHandlers.delete(handler)
  }

  /**
   * Register handler for message chunk events (streaming)
   * @param {Function} handler - Callback function(data)
   * @returns {Function} Unsubscribe function
   */
  const onMessageChunk = (handler) => {
    messageChunkHandlers.add(handler)
    return () => messageChunkHandlers.delete(handler)
  }

  /**
   * Register handler for stream complete events
   * @param {Function} handler - Callback function(data)
   * @returns {Function} Unsubscribe function
   */
  const onStreamComplete = (handler) => {
    streamCompleteHandlers.add(handler)
    return () => streamCompleteHandlers.delete(handler)
  }

  /**
   * Register handler for error events
   * @param {Function} handler - Callback function(error)
   * @returns {Function} Unsubscribe function
   */
  const onError = (handler) => {
    errorHandlers.add(handler)
    return () => errorHandlers.delete(handler)
  }

  // Auto-disconnect on unmount
  onUnmounted(() => {
    disconnect()
  })

  return {
    // State
    isConnected,
    isAuthenticated,
    connectionError,
    currentSessionUuid,

    // Connection methods
    connect,
    disconnect,
    ping,

    // Subscription methods
    subscribeToSession,
    unsubscribeFromSession,

    // Event handlers
    onMessageCreated,
    onMessageUpdated,
    onMessageDeleted,
    onStreamStart,
    onMessageChunk,
    onStreamComplete,
    onError
  }
}
